import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TProjectSelectProps } from '@/console/features/projects/components/project-select/project-select.types';
import { useProjectSelectOptions } from '@/console/features/projects/hooks/use-project-select-options/use-project-select-options';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllProjectsQuery } from '@/shared/redux/api/assets/projects/projects-api';
import {
  ProjectFragment,
  ReadAllProjectsQuery,
} from '@/shared/types/types.generated';

export const ProjectSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  projectFilter = undefined,
  disableTags = true,
  createOption,
  getItemId,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  multiple = true,
  optionProps,
  ...rest
}: TProjectSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { getAdditionalInfo, isDisabled } =
    useProjectSelectOptions(optionProps);

  const createDefaultOption = (project: ProjectFragment) => {
    return {
      label: project.name,
      value: project.projectId,
      additionalInfo: getAdditionalInfo(project),
      disabled: isDisabled(project),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllProjectsQuery) => {
    if (!data) {
      return [];
    }

    const projects = data.readAllProjects.items;
    const validProjects = projectFilter
      ? projects.filter((project) => projectFilter(project))
      : projects;

    const options = validProjects.map((project) => _createOption(project));

    if (additionalOptions.length > 0) {
      return [...additionalOptions, ...options];
    }

    return options;
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllProjectsQuery, TOptionShape>
      name={name}
      label={t('projects:select_projects', { count: multiple ? 2 : 1 })}
      placeholder={t('projects:search_projects', { count: multiple ? 2 : 1 })}
      disableTags={disableTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllProjectsQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      {...rest}
    />
  );
};
